import {
    ButtonOrLink,
    Container,
    HamburgerSolidIcon,
    Logo,
    MobileMenu,
    NavigationList,
    Text,
    UserMenu,
} from '../../';
import { VariantProps, cva } from 'class-variance-authority';
import { ReactElement, cloneElement, useState, type JSX } from 'react';
import { twMerge } from 'tailwind-merge';
import type { ExtendedNavigationItemData } from '../../molecules/navigation-list/navigation-list';
import type { CommonProps } from '../../types';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export interface HeaderProps
    extends CommonProps,
        VariantProps<typeof HeaderStyles> {
    navigationItems: ExtendedNavigationItemData[];
    logout: () => void;
    languageSwitcher: ReactElement<{ className?: string }>;
    userAbbrev?: string;
    accountLabel?: string;
    logoutLabel?: string;
}

export const HeaderStyles = cva(
    'py-6 lg:py-12 z-40 md:min-h-48 lg:h-80 lg:mb-16'
);

export const NavBarStyles = cva(
    'pt-4 pb-2.5 z-0 absolute w-full hidden lg:block lg:pt-3 bg-black before:absolute before:w-full before:h-[88px] before:z-0 before:top-0 before:bg-black before:content-[""]'
);

export const Header = ({
    navigationItems,
    className,
    userAbbrev,
    languageSwitcher,
    logout,
    accountLabel,
    logoutLabel,
    testId = 'header',
}: HeaderProps): JSX.Element => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [showMenu, setShowMenu] = useState(false);
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    return (
        <header
            className={twMerge(HeaderStyles({ className }))}
            data-testid={testId}
            role="banner"
        >
            <Container className="flex justify-between px-6 lg:px-8">
                <Logo
                    className="relative h-[60px] lg:h-[66px]"
                    href="/"
                    venueId="my-beat"
                    overwriteScreenreaderText={t('goToHomepage') ?? undefined}
                />
                <div className="flex items-center gap-4 md:min-h-[170px] lg:items-start">
                    {userAbbrev && (
                        <div
                            className="relative lg:block"
                            onMouseEnter={() => {
                                setShowMenu(true);
                            }}
                            onMouseLeave={() => {
                                setShowMenu(false);
                            }}
                            onClick={() => {
                                if (
                                    window.matchMedia('(max-width: 1023px)')
                                        .matches
                                ) {
                                    setShowMenu(false);
                                    navigate(`/${i18n.language}/account/`);
                                }
                            }}
                        >
                            <div className="bg-avatarAnimate profile-auto-spin profile-transition flex h-11 w-11 rounded-full p-[3px]">
                                <span className="m-auto flex h-full w-full items-center justify-center rounded-full text-xl font-medium uppercase leading-6">
                                    {userAbbrev}
                                </span>
                            </div>
                            {showMenu && (
                                <UserMenu
                                    logout={logout}
                                    accountLabel={accountLabel || ''}
                                    logoutLabel={logoutLabel || ''}
                                />
                            )}
                        </div>
                    )}

                    {cloneElement(languageSwitcher, {
                        className: 'hidden lg:flex items-start md:mt-1',
                    })}

                    <div className="lg:hidden">
                        <ButtonOrLink onClick={() => setIsMobileMenuOpen(true)}>
                            <Text className="sr-only">{t('closeMenu')}</Text>
                            <HamburgerSolidIcon size="lg" />
                        </ButtonOrLink>
                    </div>
                </div>
            </Container>
            <nav
                role="navigation"
                className={twMerge(NavBarStyles({ className }))}
                data-testid="navbar"
                aria-label="Top"
            >
                <Container className="hidden lg:block">
                    <NavigationList items={navigationItems} />
                </Container>
            </nav>

            {isMobileMenuOpen && (
                <MobileMenu
                    languageSwitcher={languageSwitcher}
                    items={navigationItems}
                    open={isMobileMenuOpen}
                    onClose={() => setIsMobileMenuOpen(false)}
                    logout={logout}
                    logoutLabel={logoutLabel || ''}
                />
            )}
        </header>
    );
};
